import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';

import {StatisticsFigures, StatisticsResponse} from '../../models/statistics.model';
import {
  formatCount,
  formatDelta,
  locationColumns,
  StatisticsCategoryRow,
  StatisticsDistributionItem,
  StatisticsLocationColumn,
  toDistribution
} from '../../models/statistics-view.model';

/** Aufgeklappter Inhalt einer Tabellenzeile. */
interface RowDetail {
  origins: StatisticsDistributionItem[];
  originEvents: number;
  originNote: string;
  departures: StatisticsDistributionItem[];
  departureEvents: number;
  departureNote: string;
  stays: StatisticsDistributionItem[];
}

@Component({
  selector: 'app-statistics-category-table',
  standalone: false,
  templateUrl: './statistics-category-table.component.html',
  styleUrls: ['./statistics-category-table.component.scss']
})
export class StatisticsCategoryTableComponent implements OnChanges {
  @Input({required: true}) response!: StatisticsResponse;
  @Input() rows: StatisticsCategoryRow[] = [];
  @Input() startLabel = '';
  @Input() endLabel = '';
  @Input() sortBySize = true;
  @Input() hideEmptyCategories = false;
  @Input() compareWithPreviousYear = false;

  /** Der Hinweis-Badge einer Zeile fuehrt in den Datenqualitaets-Bereich. */
  @Output() showDataQuality = new EventEmitter<void>();

  visibleRows: StatisticsCategoryRow[] = [];
  columns: StatisticsLocationColumn[] = [];
  details = new Map<string, RowDetail>();

  /** Nur zum Erzeugen der gleich breiten <col>-Elemente der Zahlenspalten. */
  numberColumnIndexes: number[] = [];

  /** Von p-table verwaltet: welche Zeilen aufgeklappt sind. */
  expandedRows: Record<string, boolean> = {};

  hiddenEmptyCount = 0;

  constructor(private readonly _translocoService: TranslocoService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['response']) {
      this.columns = locationColumns(this.response);
      this.numberColumnIndexes = Array.from({length: this.numberColumnCount}, (_, index) => index);
    }
    if (changes['rows'] || changes['sortBySize'] || changes['hideEmptyCategories']) {
      this.applyRows();
    }
    if (changes['rows'] || changes['response']) {
      this.buildDetails();
    }
  }

  /** Spaltenzahl der Tabelle, fuer den colspan der aufgeklappten Zeile. */
  get columnCount(): number {
    // Tiergruppe + Bilanz (4) + Aufenthaltsorte + Zugangsjahr (2)
    return 1 + this.numberColumnCount;
  }

  /** Alle Zahlenspalten: Bilanz (4) + Aufenthaltsorte + Zugangsjahr (2). */
  get numberColumnCount(): number {
    return 4 + this.columns.length + 2;
  }


  get currentYearLabel(): string {
    return this._translocoService.translate('statistics.table.fromCurrentYear', {year: this.response.year});
  }

  /** Veraenderung gegenueber dem Vorjahr, "–" wenn kein Vorjahr vorliegt. */
  delta(row: StatisticsCategoryRow, field: keyof StatisticsFigures): string {
    if (!row.previous) {
      return '–';
    }
    const current = row.figures[field] as number;
    const previous = row.previous[field] as number;
    return formatDelta(current - previous);
  }

  private applyRows(): void {
    const categories = this.rows.filter(row => !row.isTotal);
    const total = this.rows.find(row => row.isTotal);

    let visible = [...categories];
    if (this.sortBySize) {
      visible.sort((a, b) =>
        b.figures.stockAtPeriodEnd - a.figures.stockAtPeriodEnd
        || b.figures.admissions - a.figures.admissions
      );
    }

    this.hiddenEmptyCount = this.hideEmptyCategories ? visible.filter(row => row.isEmpty).length : 0;
    if (this.hideEmptyCategories) {
      visible = visible.filter(row => !row.isEmpty);
    }

    // Die Gesamtzeile steht immer am Ende und laesst sich ebenso aufklappen.
    this.visibleRows = total ? [...visible, total] : visible;
  }

  private buildDetails(): void {
    const t = (key: string, params?: object) => this._translocoService.translate(`statistics.detail.${key}`, params);
    this.details = new Map();

    this.rows.forEach(row => {
      const figures = row.figures;
      const whereabouts = figures.whereaboutsByType;
      const departureDiff = figures.departureEvents - figures.departures;

      this.details.set(row.key, {
        origins: toDistribution(figures.admissionsByOriginType),
        originEvents: figures.admissionEvents,
        originNote: figures.returnsFromTrialOrFoster > 0
          ? t('originNoteReturns', {
            admissions: formatCount(figures.admissions),
            returns: formatCount(figures.returnsFromTrialOrFoster)
          })
          : t('originNotePlain', {admissions: formatCount(figures.admissions)}),
        departures: toDistribution(whereabouts.filter(entry => entry.isDeparture)),
        departureEvents: figures.departureEvents,
        departureNote: departureDiff > 0
          ? t('departureNoteDiff', {
            departures: formatCount(figures.departures),
            diff: formatCount(departureDiff)
          })
          : t('departureNotePlain', {departures: formatCount(figures.departures)}),
        stays: toDistribution(whereabouts.filter(entry => !entry.isDeparture))
      });
    });
  }
}
